<script setup lang="ts">
import type { ClientDto, ProjectDto } from '@schwrz/shared';
import { computed, watch } from 'vue';

import { useClientsStore } from '../../stores/clients';
import { useProjectsStore } from '../../stores/projects';

const projectId = defineModel<string>({ default: '' });
const clientId = defineModel<string>('clientId', { default: '' });

const props = withDefaults(
  defineProps<{
    excludeProjectId?: string | null;
    autoSelectSingle?: boolean;
    disabled?: boolean;
    clientLabel?: string;
    projectLabel?: string;
  }>(),
  {
    excludeProjectId: null,
    autoSelectSingle: true,
    disabled: false,
    clientLabel: 'Компания',
    projectLabel: 'Проект',
  },
);

const clientsStore = useClientsStore();
const projectsStore = useProjectsStore();

const hintId = 'client-project-select-project-hint';

function liveProjectsForClient(cid: string): ProjectDto[] {
  return projectsStore
    .ofClient(cid)
    .filter((p) => !p.archived && p.id !== props.excludeProjectId)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'ru'));
}

function clientHasLiveProjects(c: ClientDto): boolean {
  return liveProjectsForClient(c.id).length > 0;
}

const eligibleClients = computed(() => {
  const base = clientsStore.sorted.filter((c) => !c.archived && clientHasLiveProjects(c));

  if (clientId.value && !base.some((c) => c.id === clientId.value)) {
    const extra =
      clientsStore.getByRef(clientId.value) ??
      clientsStore.archivedItems.find((c) => c.id === clientId.value);
    if (extra) return [extra, ...base];
  }

  return base;
});

const projectOptions = computed(() => {
  if (!clientId.value) return [];

  const live = liveProjectsForClient(clientId.value);

  if (projectId.value) {
    const current = projectsStore.items.find((p) => p.id === projectId.value);
    if (
      current &&
      current.clientId === clientId.value &&
      !live.some((p) => p.id === current.id)
    ) {
      return [...live, current].sort(
        (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'ru'),
      );
    }
  }

  return live;
});

const noEligibleClients = computed(
  () => eligibleClients.value.length === 0 && !clientId.value,
);

const controlsDisabled = computed(() => props.disabled || noEligibleClients.value);

const clientPlaceholder = computed(() =>
  noEligibleClients.value ? 'Нет активных проектов' : 'Выберите компанию',
);

const projectPlaceholder = computed(() => {
  if (!clientId.value) return 'Сначала выберите компанию';
  if (projectOptions.value.length === 0) return 'Нет доступных проектов';
  return 'Выберите проект';
});

function applyClientProjectRules(fromUserChange: boolean): void {
  const projects = projectOptions.value;

  if (props.autoSelectSingle && projects.length === 1) {
    const only = projects[0];
    if (only) projectId.value = only.id;
    return;
  }

  if (fromUserChange && projects.length > 1) {
    projectId.value = '';
    return;
  }

  if (projectId.value && !projects.some((p) => p.id === projectId.value)) {
    projectId.value = '';
  }
}

function onClientChanged(): void {
  applyClientProjectRules(true);
}

watch(clientId, () => {
  applyClientProjectRules(false);
});

watch(projectId, (next) => {
  if (!next) return;
  const project = projectsStore.items.find((p) => p.id === next);
  if (project && project.clientId !== clientId.value) {
    clientId.value = project.clientId;
  }
});

watch(
  () => props.excludeProjectId,
  () => {
    applyClientProjectRules(false);
  },
);
</script>

<template>
  <div
    class="client-project-select"
    :class="{ 'client-project-select--empty': noEligibleClients }"
  >
    <label class="client-project-select__field">
      <span class="client-project-select__label">{{ clientLabel }}</span>
      <select
        v-model="clientId"
        class="client-project-select__control"
        :aria-label="clientLabel"
        :disabled="controlsDisabled"
        @change="onClientChanged"
      >
        <option value="">
          {{ clientPlaceholder }}
        </option>
        <option
          v-for="c in eligibleClients"
          :key="c.id"
          :value="c.id"
        >
          {{ c.name }}
        </option>
      </select>
    </label>

    <label class="client-project-select__field">
      <span class="client-project-select__label">{{ projectLabel }}</span>
      <select
        v-model="projectId"
        class="client-project-select__control"
        :aria-label="projectLabel"
        :aria-describedby="hintId"
        :disabled="controlsDisabled || !clientId"
      >
        <option value="">
          {{ projectPlaceholder }}
        </option>
        <option
          v-for="p in projectOptions"
          :key="p.id"
          :value="p.id"
        >
          {{ p.name }}
        </option>
      </select>
    </label>

    <p
      :id="hintId"
      class="client-project-select__hint"
    >
      {{
        noEligibleClients
          ? 'Нет активных проектов'
          : 'Список проектов зависит от выбранной компании.'
      }}
    </p>
  </div>
</template>

<style scoped>
.client-project-select {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.client-project-select__field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1 1 10rem;
  min-width: 0;
  margin: 0;
}

.client-project-select__label {
  font-size: 0.75rem;
  color: var(--muted);
}

.client-project-select__control {
  width: 100%;
  font: inherit;
  font-family: var(--font-ui);
  padding: 0.45rem 0.55rem;
  border-radius: 8px;
  border: 1px solid var(--divider);
  background: var(--fill-subtle);
  color: var(--text);
}

.client-project-select__hint {
  flex: 1 1 100%;
  margin: 0;
  font-size: 0.72rem;
  color: var(--muted);
}

@media (max-width: 600px) {
  .client-project-select__field {
    flex-basis: 100%;
  }
}
</style>
